import { lazy, Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'

import { isDemoPresentation } from '@/lib/constants'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { reportQueries } from '@/features/report/queries'

import { getCurrentTenant } from '@/shared/auth/store'

import { ErrorBoundary } from '@/components/common/error-boundary'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * The demo has no report PDF to hand out (ADR 0015 dec. 5), so it swaps the
 * download list for a rendered preview + book-a-demo gate. Branching the LAZY
 * TARGET, not the render, keeps the gate out of the production graph (P-21):
 * `VITE_INCLUDE_DEMO` is a build-time literal (folded in `vite.config.ts`) and
 * comes FIRST so a non-demo build proves the branch dead and drops the chunk —
 * `isDemoPresentation` flows through a zod parse and can't do that job alone.
 */
const AirReportBody = lazy(() => {
	if (import.meta.env.VITE_INCLUDE_DEMO === 'true' && isDemoPresentation) {
		return import('@/features/air/components/demo-report-preview')
	}

	return import('@/features/report/components/air/list')
})

export const Route = createFileRoute('/_dashboard/report/air')({
	loader: ({ context: { queryClient } }) => {
		// The demo preview never lists report files, so prefetching them would be
		// a request for data nothing renders. It fetches the overview contract for
		// its own period instead — cold, since the previous calendar month is not
		// a window the overview page ever asks for, which is what the demo
		// skeleton below covers.
		if (isDemoPresentation) return

		const tenant = getCurrentTenant()

		if (tenant) {
			queryClient.prefetchQuery(reportQueries.airReport(tenant))
		}
	},

	head: () => ({
		meta: [
			{
				title: 'Air Reports | BitLyft AIR®',
			},
		],
	}),

	component: RouteComponent,
	pendingComponent: ReportSkeleton,
	errorComponent: RouteErrorComponent,
})

function RouteComponent() {
	return (
		<ErrorBoundary
			feature="report"
			section={isDemoPresentation ? 'demo-gate' : 'air-list'}
		>
			<Suspense fallback={<ReportSkeleton />}>
				<AirReportBody />
			</Suspense>
		</ErrorBoundary>
	)
}

/** P-05: mirrors whichever body the branch above mounted — list rows or the one preview card. */
function ReportSkeleton() {
	if (isDemoPresentation) {
		// 309px measured on the built card at its full height — the metric rows,
		// the blurred remainder and the CTA footer.
		return <Skeleton className="h-[310px] w-full max-w-2xl" />
	}

	return (
		<div className="flex flex-col items-start justify-start gap-4">
			<Skeleton className="h-[170px] w-full" />
			<Skeleton className="h-[170px] w-full" />
			<Skeleton className="h-[170px] w-full" />
		</div>
	)
}
